import React, { useState, useEffect } from 'react';
import { User, Lock, Bell, Save, Eye, EyeOff } from 'lucide-react';
import { MainLayout } from '../../components/layout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import userProfileService, { UserProfile, UpdateProfileRequest, ChangePasswordRequest } from '../../services/user-profile.service';

type TabType = 'profile' | 'security' | 'preferences';

const ProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState<UpdateProfileRequest>({});
  
  // Username availability state
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameMessage, setUsernameMessage] = useState<string>('');
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  // Password form state
  const [passwordForm, setPasswordForm] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState({
    notifications_enabled: true,
    language: 'en',
    theme: 'system',
  });

  useEffect(() => {
    loadProfile();
    loadPreferences();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
      }
    };
  }, [usernameCheckTimeout]);

  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem('user_preferences');
      if (saved) {
        setPreferences(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userProfileService.getMyProfile();
      setProfile(data);
      setProfileForm({
        username: data.username,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        address1: data.address1,
        address2: data.address2,
        postal_code: data.postal_code,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setUsernameMessage('');
      return;
    }

    // Don't check if username hasn't changed
    if (username === profile?.username) {
      setUsernameAvailable(true);
      setUsernameMessage('Current username');
      return;
    }

    try {
      setUsernameChecking(true);
      const result = await userProfileService.checkUsernameAvailability(username);
      setUsernameAvailable(result.success);
      setUsernameMessage(result.message);
    } catch (err) {
      setUsernameAvailable(false);
      setUsernameMessage('Error checking username');
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleUsernameChange = (value: string) => {
    setProfileForm({ ...profileForm, username: value });
    
    // Clear previous timeout
    if (usernameCheckTimeout) {
      clearTimeout(usernameCheckTimeout);
    }
    
    // Debounce username availability check
    const timeoutId = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);
    
    setUsernameCheckTimeout(timeoutId);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      await userProfileService.updateMyProfile(profileForm);
      setSuccessMessage('Profile updated successfully');
      
      // Clear username availability messages
      setUsernameAvailable(null);
      setUsernameMessage('');
      
      await loadProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      await userProfileService.changePassword(passwordForm);
      setSuccessMessage('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '' });
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handlePreferencesUpdate = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      // Store preferences in localStorage (backend API not yet implemented)
      localStorage.setItem('user_preferences', JSON.stringify(preferences));
      
      setSuccessMessage('Preferences saved locally');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'profile', label: 'Profile', icon: <User size={18} /> },
    { id: 'security', label: 'Security', icon: <Lock size={18} /> },
    { id: 'preferences', label: 'Preferences', icon: <Bell size={18} /> },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout>
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">My Profile</h1>
        <p className="text-sm text-text-muted mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-status-danger/10 border border-status-danger/20 rounded-lg text-status-danger">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-status-success/10 border border-status-success/20 rounded-lg text-status-success">
          {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-secondary mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors relative ${
              activeTab === tab.id
                ? 'text-accent-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-primary" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    First Name *
                  </label>
                  <Input
                    type="text"
                    value={profileForm.first_name || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Last Name *
                  </label>
                  <Input
                    type="text"
                    value={profileForm.last_name || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={profileForm.email || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={profileForm.username || ''}
                      onChange={(e) => handleUsernameChange(e.target.value)}
                      placeholder="Choose a unique username"
                      minLength={3}
                      maxLength={50}
                    />
                    {usernameChecking && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-accent-primary border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  {usernameMessage && (
                    <p className={`text-sm mt-1 ${usernameAvailable ? 'text-status-success' : 'text-status-danger'}`}>
                      {usernameMessage}
                    </p>
                  )}
                  <p className="text-xs text-text-muted mt-1">
                    You can login with either your username or email address
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    value={profileForm.phone || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Address Line 1
                  </label>
                  <Input
                    type="text"
                    value={profileForm.address1 || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, address1: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Address Line 2
                  </label>
                  <Input
                    type="text"
                    value={profileForm.address2 || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, address2: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Postal Code
                  </label>
                  <Input
                    type="text"
                    value={profileForm.postal_code || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })}
                  />
                </div>
              </div>

              {/* Company Info (Read-only) */}
              <div className="pt-6 border-t border-surface-secondary">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">Company</label>
                    <div className="text-text-primary">{profile?.company_name}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">Role</label>
                    <div className="text-text-primary">{profile?.role_name || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={loadProfile}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <Card>
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Current Password *
                  </label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    New Password *
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Confirm New Password *
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPasswordForm({ current_password: '', new_password: '' });
                    setConfirmPassword('');
                  }}
                >
                  Clear
                </Button>
                <Button type="submit" disabled={saving}>
                  <Lock size={18} />
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <Card>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-surface-secondary rounded-lg">
                <div>
                  <h3 className="font-medium text-text-primary">Enable Notifications</h3>
                  <p className="text-sm text-text-muted">Receive notifications for important events</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.notifications_enabled}
                    onChange={(e) => setPreferences({ ...preferences, notifications_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-hover peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-primary"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Language
                </label>
                <select
                  value={preferences.language}
                  onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                  className="w-full px-4 py-2 bg-surface-secondary border border-surface-hover rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="en" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>English</option>
                  <option value="fr" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>French</option>
                  <option value="es" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>Spanish</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Theme
                </label>
                <select
                  value={preferences.theme}
                  onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                  className="w-full px-4 py-2 bg-surface-secondary border border-surface-hover rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="system" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>System Default</option>
                  <option value="light" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>Light</option>
                  <option value="dark" style={{ backgroundColor: '#1e293b', color: '#e2e8f0' }}>Dark</option>
                </select>
              </div>

              <div className="flex justify-end">
                <Button onClick={handlePreferencesUpdate} disabled={saving}>
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
    </MainLayout>
  );
};

export default ProfilePage;
